/**
 * DocumentPrintTemplate.jsx — Feb 2026
 *
 * The single, canonical print entry point for Proforma Invoice (PI),
 * Sales Order (SO), and Purchase Order (PO). All three modules import
 * `printDocument` (and, for the print button, `<PrintDocumentButton>`)
 * from THIS file — no other place in the codebase should build its own
 * blob URL or window.open() call for these three documents.
 *
 * The heavy lifting (letterhead, Bill/Ship blocks, line-items, GST /
 * qty totals, watermarks) lives on the backend at
 *   • GET /api/proforma-invoices/{id}/print.html
 *   • GET /api/sales-orders/{id}/print.html
 *   • GET /api/purchase-orders/{id}/print.html
 * Every response is rendered by the SAME `render_pi_style_html()` on
 * the server, so a fix to the layout in ONE place propagates to all
 * three modules instantly.
 *
 * Safari CSS guard: Safari 15/16 renders `@page` margin boxes as a full
 * extra blank page in some contexts. We solve that end-to-end:
 *   1. Backend template avoids `@page` margin boxes and fixes the sheet
 *      to a plain 210mm A4 (see `render_pi_style_html`).
 *   2. Frontend serves the HTML via a `Blob` + `URL.createObjectURL`
 *      (NOT `document.write`, which Safari blanks after `window.print`).
 *   3. Popup blockers are respected — we call `window.open` synchronously
 *      inside the user gesture BEFORE the network fetch, then swap
 *      `location.href` when the blob is ready. Result: no blank first
 *      page and no popup-blocked toast on Safari, Chrome or Edge.
 */
import { toast } from "sonner";
import { PrinterIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

const KIND_TO_PATH = {
  pi:              (id) => `/proforma-invoices/${id}/print.html`,
  "proforma-invoices": (id) => `/proforma-invoices/${id}/print.html`,
  so:              (id) => `/sales-orders/${id}/print.html`,
  "sales-orders":  (id) => `/sales-orders/${id}/print.html`,
  po:              (id) => `/purchase-orders/${id}/print.html`,
  "purchase-orders": (id) => `/purchase-orders/${id}/print.html`,
};

/**
 * printDocument({ kind, id })
 *
 * @param {"pi"|"so"|"po"|string} kind — document family
 * @param {string} id                  — Mongo document id
 * Opens the browser-print A4 HTML view in a new tab. Safari-safe.
 */
export async function printDocument({ kind, id }) {
  const pathFn = KIND_TO_PATH[kind];
  if (!pathFn || !id) {
    toast.error("Print unavailable for this document");
    return;
  }
  // Open the popup synchronously in the user gesture so Safari does NOT
  // classify it as a blocked popup. We swap `location.href` once the
  // fetch resolves.
  const win = window.open("about:blank", "_blank");
  if (!win) {
    toast.error("Popup blocked — please allow popups for this site");
    return;
  }
  // Show a tiny loading shell inside the popup so the user gets instant
  // visual feedback while the HTML fetch is in flight.
  try {
    win.document.write(
      "<title>Preparing print…</title>" +
      "<style>body{font-family:system-ui;padding:2rem;color:#333}</style>" +
      "<h3>Preparing print…</h3><p>Fetching the document from the server.</p>"
    );
    win.document.close();
  } catch { /* cross-origin safety */ }

  try {
    const res = await api.get(pathFn(id), { responseType: "text" });
    const blob = new Blob([res.data], { type: "text/html;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    win.location.href = url;
    // Give Safari time to load the blob before we revoke the URL.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    try { win.close(); } catch { /* noop */ }
    // PO-GST-01 · P2-D3 · text responses carry JSON errors (403 / 409 PO_PRINT_SNAPSHOT_INVALID).
    let e = err;
    const raw = err?.response?.data;
    if (typeof raw === "string") { try { e = { ...err, response: { ...err.response, data: JSON.parse(raw) } }; } catch { /* keep */ } }
    toast.error(apiErrorMessage(e, "Print failed"));
  }
}

/**
 * <PrintDocumentButton kind="so" id={o.id} label="Print / PDF" />
 * Ready-made "Print / PDF" button so PI / SO / PO share the same
 * affordance and copy. Style overrides via `className`.
 */
export function PrintDocumentButton({ kind, id, label = "Print / PDF", className = "", disabled = false, testId }) {
  const cls = className || "px-4 py-2 rounded-sm border border-[var(--accent)] text-[var(--accent)] text-sm font-semibold flex items-center gap-2 hover:bg-[var(--accent)]/10 transition disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <button
      type="button"
      onClick={() => printDocument({ kind, id })}
      data-testid={testId || `print-${kind}-${id || "doc"}`}
      className={cls}
      disabled={disabled}
    >
      <PrinterIcon size={14} weight="bold" />
      {label}
    </button>
  );
}

export default printDocument;
